import React, { useState } from 'react';
import SafeImage from '../SafeImage';
import { uploadLectureSolvedImage, deleteLectureSolvedImage } from '../../utils/lectureStorage';
import {
  blankQuizMcq,
  importQuizJson,
  normalizeQuizOption,
  quizQuestionsFromDocx,
  sampleQuizJsonText,
} from '../../utils/authoredQuiz';
import { parsePracticeExamDocx } from '../../utils/practiceExamDocx';

export default function QuizMcqEditor({
  questions = [],
  onChange,
  label = 'Quiz questions',
  hint = 'Add or edit multiple-choice questions here, or upload a JSON file. Students take these stored questions — no AI quiz is generated.',
  allowMedia = false,
  moduleId = '',
}) {
  const [jsonError, setJsonError] = useState('');
  const [docxStatus, setDocxStatus] = useState('');
  const [uploadingKey, setUploadingKey] = useState(null);
  const list = questions?.length ? questions : [blankQuizMcq()];

  function setList(next) {
    onChange(typeof next === 'function' ? next(list) : next);
  }

  function optionAt(q, optIdx) {
    return normalizeQuizOption(q?.options?.[optIdx]);
  }

  function updateQuestion(index, patch) {
    setList(list.map((q, i) => (i === index ? { ...q, ...patch } : q)));
  }

  function updateOption(index, optIdx, patch) {
    setList(
      list.map((q, i) => {
        if (i !== index) return q;
        const options = [0, 1, 2, 3].map((j) => optionAt(q, j));
        options[optIdx] = { ...options[optIdx], ...patch };
        return { ...q, options };
      })
    );
  }

  function updateExplanation(index, optIdx, value) {
    setList(
      list.map((q, i) => {
        if (i !== index) return q;
        const explanations = [...(q.explanations || ['', '', '', ''])];
        while (explanations.length < 4) explanations.push('');
        explanations[optIdx] = value;
        const next = { ...q, explanations };
        if (optIdx === Number(q.correct)) next.explanation = value;
        return next;
      })
    );
  }

  function addQuestion() {
    setList([...list, blankQuizMcq()]);
  }

  function removeQuestion(index) {
    if (list.length <= 1) {
      setList([blankQuizMcq()]);
      return;
    }
    setList(list.filter((_, i) => i !== index));
  }

  function moveQuestion(index, dir) {
    const next = index + dir;
    if (next < 0 || next >= list.length) return;
    const copy = [...list];
    const [item] = copy.splice(index, 1);
    copy.splice(next, 0, item);
    setList(copy);
  }

  async function uploadImage(key, file, apply) {
    if (!file || !moduleId) {
      alert('Save or select a module first, then upload images from your computer.');
      return;
    }
    setUploadingKey(key);
    try {
      const { url, path } = await uploadLectureSolvedImage(file, moduleId, 'quiz');
      apply(url, path);
    } catch (err) {
      alert(err.message || 'Image upload failed');
    } finally {
      setUploadingKey(null);
    }
  }

  function handleJsonFile(e) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const imported = importQuizJson(String(reader.result || ''));
        setJsonError('');
        onChange(imported.questions);
      } catch (err) {
        setJsonError(err.message || 'Could not read this JSON file.');
      }
    };
    reader.onerror = () => setJsonError('Could not read this JSON file.');
    reader.readAsText(file);
  }

  function downloadSample() {
    const blob = new Blob([sampleQuizJsonText()], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'quiz-sample.json';
    a.click();
    URL.revokeObjectURL(url);
  }

  async function handleDocxFile(e) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!moduleId) {
      setJsonError('Save or select a module first, then upload the document.');
      return;
    }
    setJsonError('');
    setDocxStatus('Reading document…');
    try {
      const parsed = await parsePracticeExamDocx(await file.arrayBuffer());
      const imported = await quizQuestionsFromDocx(parsed, async (imageFile) => {
        setDocxStatus('Uploading images…');
        return uploadLectureSolvedImage(imageFile, moduleId, 'quiz');
      });
      onChange(imported);
      setDocxStatus(`Imported ${imported.length} question${imported.length === 1 ? '' : 's'}. Save the module to keep them.`);
    } catch (err) {
      setDocxStatus('');
      setJsonError(err.message || 'Could not import that document.');
    }
  }

  return (
    <div>
      <div style={st.toolbar}>
        <div>
          <label style={st.formLabel}>{label}</label>
          <p style={st.hint}>{hint}</p>
        </div>
        <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
          <button type="button" onClick={downloadSample} style={st.ghostBtn}>
            Sample JSON
          </button>
          <label style={{ ...st.ghostBtn, cursor: 'pointer' }}>
            Upload JSON
            <input type="file" accept="application/json,.json" onChange={handleJsonFile} style={{ display: 'none' }} />
          </label>
          {allowMedia ? (
            <button
              type="button"
              onClick={() => {
                const link = document.createElement('a');
                link.href = `${process.env.PUBLIC_URL || ''}/module-exam-sample.docx`;
                link.download = 'module-exam-sample.docx';
                link.click();
              }}
              style={st.ghostBtn}
            >
              Sample DOCX
            </button>
          ) : null}
          {allowMedia ? (
            <label style={{ ...st.ghostBtn, cursor: docxStatus.startsWith('Reading') || docxStatus.startsWith('Uploading') ? 'wait' : 'pointer' }}>
              Upload DOCX
              <input
                type="file"
                accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                onChange={handleDocxFile}
                style={{ display: 'none' }}
              />
            </label>
          ) : null}
        </div>
      </div>
      {jsonError ? <p style={st.error}>{jsonError}</p> : null}
      {docxStatus ? <p style={st.hint}>{docxStatus}</p> : null}

      {list.map((q, qIdx) => (
        <div key={qIdx} style={st.card}>
          <div style={st.cardHead}>
            <strong style={{ fontSize: 13 }}>Question {qIdx + 1}</strong>
            <div style={{ display: 'flex', gap: 6 }}>
              <button type="button" style={st.iconBtn} onClick={() => moveQuestion(qIdx, -1)} disabled={qIdx === 0}>
                ↑
              </button>
              <button
                type="button"
                style={st.iconBtn}
                onClick={() => moveQuestion(qIdx, 1)}
                disabled={qIdx === list.length - 1}
              >
                ↓
              </button>
              <button type="button" style={{ ...st.iconBtn, color: '#dc2626' }} onClick={() => removeQuestion(qIdx)}>
                Delete
              </button>
            </div>
          </div>
          <textarea
            style={st.textarea}
            rows={2}
            value={q.question || ''}
            onChange={(e) => updateQuestion(qIdx, { question: e.target.value })}
            placeholder="Write the question students will see."
          />

          {allowMedia ? (
            <div style={{ marginTop: 10 }}>
              <label style={st.formLabel}>Question image (optional, from your computer)</label>
              {q.questionImageUrl ? (
                <div style={{ marginBottom: 8 }}>
                  <SafeImage src={q.questionImageUrl} alt="" style={st.preview} />
                  <button
                    type="button"
                    style={st.removeImg}
                    onClick={async () => {
                      await deleteLectureSolvedImage(q.questionImagePath);
                      updateQuestion(qIdx, { questionImageUrl: '', questionImagePath: '' });
                    }}
                  >
                    Remove question image
                  </button>
                </div>
              ) : null}
              <label style={st.uploadBtn}>
                {uploadingKey === `q-${qIdx}` ? 'Uploading…' : 'Upload question image'}
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif,.jpg,.jpeg,.png,.webp,.gif"
                  disabled={uploadingKey != null}
                  onChange={(e) => {
                    const files = Array.from(e.target.files || []);
                    e.target.value = '';
                    if (files[0]) {
                      uploadImage(`q-${qIdx}`, files[0], (url, path) =>
                        updateQuestion(qIdx, { questionImageUrl: url, questionImagePath: path })
                      );
                    }
                  }}
                  style={st.fileInput}
                />
              </label>
              <textarea
                style={{ ...st.textarea, marginTop: 8, minHeight: 56, fontSize: 13 }}
                rows={2}
                value={q.questionImageDescription || ''}
                onChange={(e) => updateQuestion(qIdx, { questionImageDescription: e.target.value })}
                placeholder="Optional description of the figure (for the tutor and as a student caption)."
              />
            </div>
          ) : null}

          {[0, 1, 2, 3].map((optIdx) => {
            const opt = optionAt(q, optIdx);
            return (
              <div key={optIdx} style={{ marginTop: 8 }}>
                <label style={st.optionRow}>
                  <input
                    type="radio"
                    name={`quiz-correct-${qIdx}`}
                    checked={Number(q.correct) === optIdx}
                    onChange={() => updateQuestion(qIdx, { correct: optIdx })}
                  />
                  <span style={st.letter}>{String.fromCharCode(65 + optIdx)}</span>
                  <input
                    style={st.input}
                    value={opt.text || ''}
                    onChange={(e) => updateOption(qIdx, optIdx, { text: e.target.value })}
                    placeholder={`Option ${String.fromCharCode(65 + optIdx)} text${allowMedia ? ' (or upload an image)' : ''}`}
                  />
                </label>
                {allowMedia ? (
                  <div style={{ marginLeft: 28, marginTop: 6 }}>
                    {opt.imageUrl ? (
                      <div>
                        <SafeImage src={opt.imageUrl} alt={opt.alt || ''} style={st.optPreview} />
                        <button
                          type="button"
                          style={st.removeImg}
                          onClick={async () => {
                            await deleteLectureSolvedImage(opt.imagePath);
                            updateOption(qIdx, optIdx, { imageUrl: '', imagePath: '', alt: '' });
                          }}
                        >
                          Remove option image
                        </button>
                      </div>
                    ) : (
                      <label style={{ ...st.uploadBtn, padding: '6px 10px', fontSize: 11 }}>
                        {uploadingKey === `o-${qIdx}-${optIdx}` ? 'Uploading…' : 'Upload option image'}
                        <input
                          type="file"
                          accept="image/jpeg,image/png,image/webp,image/gif,.jpg,.jpeg,.png,.webp,.gif"
                          disabled={uploadingKey != null}
                          onChange={(e) => {
                            const files = Array.from(e.target.files || []);
                            e.target.value = '';
                            if (files[0]) {
                              uploadImage(`o-${qIdx}-${optIdx}`, files[0], (url, path) =>
                                updateOption(qIdx, optIdx, { imageUrl: url, imagePath: path, alt: opt.text || files[0].name })
                              );
                            }
                          }}
                          style={st.fileInput}
                        />
                      </label>
                    )}
                  </div>
                ) : null}
                <textarea
                  style={{ ...st.textarea, marginTop: 6, minHeight: 56, fontSize: 13 }}
                  rows={2}
                  value={q.explanations?.[optIdx] || (Number(q.correct) === optIdx ? q.explanation || '' : '')}
                  onChange={(e) => updateExplanation(qIdx, optIdx, e.target.value)}
                  placeholder={
                    Number(q.correct) === optIdx
                      ? 'Why this option is correct (shown after submit).'
                      : `Why ${String.fromCharCode(65 + optIdx)} is wrong (optional).`
                  }
                />
              </div>
            );
          })}
        </div>
      ))}

      <button type="button" onClick={addQuestion} style={st.addBtn}>
        Add question
      </button>
    </div>
  );
}

const st = {
  formLabel: {
    display: 'block',
    fontSize: 11,
    fontWeight: 700,
    color: '#64748b',
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    marginBottom: 6,
  },
  hint: { fontSize: 12, color: '#64748b', lineHeight: 1.45, margin: '0 0 8px' },
  error: {
    fontSize: 13,
    color: '#b91c1c',
    background: '#fef2f2',
    borderRadius: 8,
    padding: '8px 10px',
    marginBottom: 12,
  },
  toolbar: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: 12,
    alignItems: 'flex-start',
    marginBottom: 12,
  },
  ghostBtn: {
    fontSize: 12,
    fontWeight: 700,
    color: '#334155',
    background: '#f1f5f9',
    border: 'none',
    borderRadius: 8,
    padding: '8px 10px',
  },
  uploadBtn: {
    position: 'relative',
    display: 'inline-flex',
    overflow: 'hidden',
    fontSize: 12,
    fontWeight: 700,
    color: '#fff',
    background: '#0f766e',
    borderRadius: 8,
    padding: '8px 12px',
    cursor: 'pointer',
  },
  fileInput: {
    position: 'absolute',
    inset: 0,
    opacity: 0,
    cursor: 'pointer',
    fontSize: 24,
  },
  preview: {
    width: '100%',
    maxHeight: 160,
    objectFit: 'contain',
    borderRadius: 10,
    border: '1px solid #e2e8f0',
    background: '#fff',
    display: 'block',
    marginBottom: 6,
  },
  optPreview: {
    width: '100%',
    maxHeight: 100,
    objectFit: 'contain',
    borderRadius: 8,
    border: '1px solid #e2e8f0',
    background: '#fff',
    display: 'block',
    marginBottom: 6,
  },
  removeImg: {
    fontSize: 11,
    fontWeight: 700,
    color: '#dc2626',
    border: 'none',
    background: '#fef2f2',
    borderRadius: 8,
    padding: '6px 8px',
    cursor: 'pointer',
  },
  card: {
    border: '1px solid #e2e8f0',
    borderRadius: 12,
    padding: 14,
    marginBottom: 12,
    background: '#f8fafc',
  },
  cardHead: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 10,
  },
  iconBtn: {
    fontSize: 12,
    fontWeight: 700,
    border: 'none',
    background: '#fff',
    padding: '6px 8px',
    borderRadius: 8,
    cursor: 'pointer',
  },
  optionRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  letter: { fontSize: 12, fontWeight: 700, color: '#64748b', width: 16 },
  input: {
    flex: 1,
    width: '100%',
    padding: '10px 12px',
    borderRadius: 10,
    border: '1.5px solid #e2e8f0',
    fontSize: 14,
    boxSizing: 'border-box',
    margin: 0,
  },
  textarea: {
    width: '100%',
    padding: '10px 12px',
    borderRadius: 10,
    border: '1.5px solid #e2e8f0',
    fontSize: 14,
    lineHeight: 1.5,
    boxSizing: 'border-box',
    resize: 'vertical',
    fontFamily: 'inherit',
  },
  addBtn: {
    border: 'none',
    background: '#0f766e',
    color: '#fff',
    fontWeight: 700,
    fontSize: 13,
    padding: '10px 14px',
    borderRadius: 10,
    cursor: 'pointer',
  },
};
